import Phaser from "phaser";
import type { TabViewContract, TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import { NineSliceButton, NINE_SLICE_BUTTON_EVENT, ScrollableList } from "@yjsgames/phaser-ui";
import { FONT_FAMILY, PRIMARY_BUTTON_STYLE, setButtonEnabled, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import type { TextDirection } from "@yjsgames/i18n";
import { computeDailyShopLayout } from "./dailyShopLayout.js";
import { TipCarouselView } from "./TipCarouselView.js";

/** 2열 × 3행 그리드 (= 6슬롯, DailyShopController SLOT_COUNT와 일치). */
const SLOT_COUNT = 6;

function setVisibleActive<T extends Phaser.GameObjects.GameObject & { setVisible: (visible: boolean) => T }>(
  obj: T,
  visible: boolean,
): void {
  obj.setVisible(visible);
  (obj as Phaser.GameObjects.GameObject & { setActive?: (value: boolean) => unknown }).setActive?.(visible);
}

/** 슬롯 1칸 표시 데이터 — GameScene.buildDailyShopData가 현지화·가격 계산 완료 후 전달. */
export interface DailyShopSlotData {
  /** 상품명 (현지화 완료). */
  readonly name: string;
  /** 보상 · 가격 요약 (현지화 완료). 할인 시 "원가 → 할인가 (-N%)". soldOut이면 무시. */
  readonly detail: string;
  readonly soldOut: boolean;
  /** 구매 가능 (잔액 충분 + sold-out 아님). */
  readonly affordable: boolean;
  /** 슬롯 아이콘 텍스처 키 (데이터 제어 — dailyShop.tsv iconKey). */
  readonly iconKey: string;
  /** 구매 버튼에 표시할 비용 통화 아이콘 키 (gold/diamond). ad 코스트면 undefined(아이콘 없이 텍스트). */
  readonly costIconKey?: string;
  /** 구매 버튼 라벨 — 비용 수량(gold/diamond) 또는 ad 텍스트("광고 시청"/"무료 수령"). */
  readonly costLabel: string;
  /** 광고 처리 중 — ad 슬롯 버튼을 준비중 상태로 표시한다. */
  readonly adProcessing?: boolean;
  /** 할인 배지 라벨("-30%") — 할인 없으면 빈 문자열(배지 숨김). */
  readonly discountLabel: string;
}

export interface DailyShopTabData {
  /** 다음 자정까지 남은 ms (리셋 카운트다운). */
  readonly msUntilReset: number;
  /** 새로고침 버튼 라벨 (현지화 완료 — 무료/N다이아/내일자정). */
  readonly rerollLabel: string;
  /** 새로고침 가능 (무료 또는 다이아 충분, 소진 시 false). */
  readonly rerollAvailable: boolean;
  /** 최근 구매 결과 (현지화 완료). 빈 값이면 안내 문구. */
  readonly resultLabel: string;
  readonly slots: readonly DailyShopSlotData[];
}

export interface DailyShopTabViewOptions {
  /** 슬롯 구매 — 통화 차감 후 보상 지급. */
  readonly onBuySlot: (index: number) => void;
  /** 새로고침 — 무료 1회 또는 다이아 누진 차감 후 슬롯 재추첨. */
  readonly onReroll: () => void;
}

/** ms → "{h}시간 {m}분" / "{m}분" 현지화 (DungeonTabView와 동일 표기). */
function formatTimeLeft(ms: number): string {
  const totalMin = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? t("time.hm", { h, m }) : t("time.m", { m });
}

/**
 * 일일상점 탭 콘텐츠 — 제목 + 자정 리셋 카운트다운 + 새로고침 버튼 + **2×3 그리드 6슬롯**(셀: 아이콘+상품명+보상·가격+구매) + 결과.
 * idleblade RandomShop 레퍼런스. 매일 자정 재추첨(@yjsgames/daily-reset 구동), 슬롯별 할인, sold-out, 무료/다이아 누진 리롤.
 * 아이콘은 데이터(dailyShop.tsv iconKey)로 제어 — 전용 이미지 준비 시 tsv만 교체.
 * 배치: 헤더 offset/grid 수치/셀 내부 offset은 dailyShop.json, RTL 열·내부 미러는 computeDailyShopLayout.
 */
export class DailyShopTabView implements TabViewContract<DailyShopTabData> {
  private readonly title: Phaser.GameObjects.Text;
  private readonly countdown: Phaser.GameObjects.Text;
  private readonly rerollBtn: NineSliceButton;
  /** 헤더 아래 팁 캐러셀 (재사용 컴포넌트, tab="dailyShop"). */
  private readonly tipView: TipCarouselView;
  /** 고정 크기 상품 셀을 담는 스크롤 컨테이너. 해상도 부족 시 셀을 압축하지 않고 스크롤한다. */
  private readonly scrollList: ScrollableList;
  private readonly resultText: Phaser.GameObjects.Text;
  private readonly cells: {
    bg: Phaser.GameObjects.Rectangle;
    icon: Phaser.GameObjects.Image;
    name: Phaser.GameObjects.Text;
    detail: Phaser.GameObjects.Text;
    buyBtn: NineSliceButton;
    discountBadge: Phaser.GameObjects.Text;
  }[] = [];
  /** 탭 표시 상태. update()가 비활성(false) 탭의 셀을 setVisible(true)로 되살리지 않도록 보존
   *  (형제 CodexTabView/ModuleGridView 패턴 — refreshAll이 비활성 탭도 update하므로 필수). */
  private visible = true;
  /** relayout(방향 전환)에서 computeDailyShopLayout 재호출용 콘텐츠 영역 + 하한. */
  private readonly rect: { x: number; y: number; width: number };
  private readonly bottomY: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    bottomY: number,
    opts: DailyShopTabViewOptions,
  ) {
    this.rect = { x, y, width };
    this.bottomY = bottomY;
    const dir = getTextDirection();
    const L = computeDailyShopLayout({ x, y, width, height: 0 }, dir, bottomY, SLOT_COUNT);
    const tox = L.textOriginX;
    const wrapAlign = dir === "rtl" ? "right" : "left";

    this.title = scene.add
      .text(L.title.x, L.title.y, t("dailyShop.title"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textSecondary,
      })
      .setOrigin(tox, 0)
      .setDepth(2);
    this.countdown = scene.add
      .text(L.countdown.x, L.countdown.y, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textDim,
      })
      .setOrigin(tox, 0)
      .setDepth(2);
    this.rerollBtn = new NineSliceButton(scene, L.rerollBtn.x, L.rerollBtn.y, {
      style: PRIMARY_BUTTON_STYLE,
      label: t("dailyShop.refreshFree"),
      widthOverride: 130,
      heightOverride: 28,
    });
    this.rerollBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onReroll());
    scene.add.existing(this.rerollBtn);

    // 헤더 아래 팁 캐러셀 — 일일상점 설명(2~3개 팁)을 랜덤 시작 + ◀/▶ 순환(재사용 컴포넌트, 전체폭).
    this.tipView = new TipCarouselView(scene, L.tip.x, L.tip.y, L.tip.width, L.tip.height, "dailyShop");
    this.tipView.setDepth(2);
    scene.add.existing(this.tipView);

    this.scrollList = new ScrollableList(scene, L.scroll.x, L.scroll.y, {
      width: L.scroll.width,
      height: L.scroll.height,
      barWidth: 5,
      barColor: PALETTE_NUM.scrollBar,
      trackColor: PALETTE_NUM.scrollTrack,
      trackAlpha: 0.65,
      barAlpha: 0.95,
      scrollSpeed: 36,
    });
    this.scrollList.setDepth(2);
    scene.add.existing(this.scrollList);

    for (let i = 0; i < SLOT_COUNT; i++) {
      const c = L.cells[i]!;
      // 셀 배경 — 스탯 탭 행 프레임과 동일 양식(rectangle + stroke). 레이아웃 bg 좌표는 top-left(origin 0,0).
      const bg = scene.add
        .rectangle(c.bg.x, c.bg.y, c.bg.width, c.bg.height, PALETTE_NUM.cellBg, 0.88)
        .setOrigin(0, 0)
        .setStrokeStyle(1, PALETTE_NUM.gridPathGlow, 0.28)
        .setDepth(1);

      const icon = scene.add
        .image(c.icon.x, c.icon.y, "icon_gold")
        .setDisplaySize(26, 26)
        .setDepth(2);
      const name = scene.add
        .text(c.name.x, c.name.y, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.tiny,
          color: PALETTE.textPrimary,
        })
        .setOrigin(L.cellTextOriginX, 0)
        .setDepth(2);
      const detail = scene.add
        .text(c.detail.x, c.detail.y, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.micro,
          color: PALETTE.accentAmber,
          align: wrapAlign,
          wordWrap: { width: c.detail.wrapWidth },
        })
        .setOrigin(L.cellTextOriginX, 0)
        .setDepth(2);
      const idx = i;
      // 버튼: [통화 아이콘] 비용 수량 — 아이콘은 update()가 슬롯 통화에 맞게 교체(기본 icon_gold).
      const buyBtn = new NineSliceButton(scene, c.buyBtn.x, c.buyBtn.y, {
        style: PRIMARY_BUTTON_STYLE,
        label: "",
        widthOverride: c.buyBtn.width,
        heightOverride: c.buyBtn.height,
        iconKey: "icon_gold",
        iconSize: 14,
      });
      buyBtn.on(NINE_SLICE_BUTTON_EVENT.Click, () => opts.onBuySlot(idx));
      buyBtn.setDepth(2); // 셀 배경(bg depth 1) 위로 — 누락 시 패널에 가려 안 보임

      // 할인 배지 — 셀 우상단(RTL 좌상단), 빨강 배경 + 흰 텍스트. update가 할인 슬롯에만 표시한다.
      const discountBadge = scene.add
        .text(c.discountBadge.x, c.discountBadge.y, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.micro,
          color: PALETTE.textBright,
          backgroundColor: PALETTE.signalDanger,
          padding: { x: 5, y: 2 },
          fontStyle: "bold",
        })
        .setOrigin(dir === "rtl" ? 0 : 1, 0)
        .setDepth(3); // 셀 콘텐츠(depth 2) 위 — 코너 배지

      this.scrollList.addContent(bg, c.bg.x, c.bg.y);
      this.scrollList.addContent(icon, c.icon.x, c.icon.y);
      this.scrollList.addContent(name, c.name.x, c.name.y);
      this.scrollList.addContent(detail, c.detail.x, c.detail.y);
      this.scrollList.addContent(buyBtn, c.buyBtn.x, c.buyBtn.y);
      this.scrollList.addContent(discountBadge, c.discountBadge.x, c.discountBadge.y);
      this.cells.push({ bg, icon, name, detail, buyBtn, discountBadge });
    }
    this.scrollList.setContentHeight(L.contentHeight);

    this.resultText = scene.add
      .text(L.result.x, L.result.y, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.positive,
        align: wrapAlign,
        wordWrap: { width },
      })
      .setOrigin(tox, 0)
      .setDepth(2);
  }

  /** 동적 표시 갱신 — 카운트다운/리롤/결과 + 2×3 셀(아이콘·상품명·보상가격·구매). 풀보다 슬롯이 적으면 남는 셀 숨김. */
  update(d: DailyShopTabData): void {
    this.countdown.setText(t("dailyShop.countdown", { time: formatTimeLeft(d.msUntilReset) }));
    this.rerollBtn.setLabel(d.rerollLabel);
    setButtonEnabled(this.rerollBtn, d.rerollAvailable);
    this.resultText.setText(d.resultLabel || t("dailyShop.hint"));
    for (let i = 0; i < this.cells.length; i++) {
      const cell = this.cells[i]!;
      const slot = d.slots[i];
      if (!slot) {
        this.setCellVisible(cell, false);
        continue;
      }
      cell.icon.setTexture(slot.iconKey);
      cell.name.setText(slot.name);
      cell.detail.setText(slot.soldOut ? t("dailyShop.soldOut") : slot.detail);
      // 구매 버튼: 비용 통화 아이콘 + 수량 (ad 코스트는 아이콘 없이 "광고 시청"/"무료 수령" 텍스트).
      if (slot.costIconKey) {
        cell.buyBtn.setIconVisible(true);
        cell.buyBtn.setIcon(slot.costIconKey);
      } else {
        cell.buyBtn.setIconVisible(false);
      }
      cell.buyBtn.setLabel(slot.adProcessing ? t("shop.adDiamond.processing") : slot.costLabel);
      this.setCellVisible(cell, this.visible);
      setButtonEnabled(cell.buyBtn, slot.affordable && !slot.soldOut && !slot.adProcessing);
      // 할인 배지 — 할인 슬롯 + 미품절일 때만. setCellVisible의 일괄 표시를 여기서 조건부로 덮는다.
      cell.discountBadge.setText(slot.discountLabel);
      setVisibleActive(cell.discountBadge, this.visible && !slot.soldOut && slot.discountLabel.length > 0);
    }
  }

  private setCellVisible(cell: DailyShopTabView["cells"][number], visible: boolean): void {
    setVisibleActive(cell.bg, visible);
    setVisibleActive(cell.icon, visible);
    setVisibleActive(cell.name, visible);
    setVisibleActive(cell.detail, visible);
    cell.buyBtn.setVisible(visible);
    setVisibleActive(cell.discountBadge, visible); // update()가 무할인 슬롯은 다시 숨김
  }

  /** 언어 변경 — 정적 제목만 갱신. 버튼 라벨은 비용 수량(또는 ad 텍스트)이라 update()가 갱신(여기서 덮지 않음). */
  applyLanguage(): void {
    this.title.setText(t("dailyShop.title"));
    this.tipView.applyLanguage();
  }

  /** 방향 전환(RTL) — computeDailyShopLayout 재호출로 헤더 + 2×3 셀 좌표/origin/align 재배치(destroy 0). */
  relayout(dir: TextDirection): void {
    const L = computeDailyShopLayout({ ...this.rect, height: 0 }, dir, this.bottomY, SLOT_COUNT);
    const tox = L.textOriginX;
    const wrapAlign = dir === "rtl" ? "right" : "left";
    this.title.setPosition(L.title.x, L.title.y).setOrigin(tox, 0);
    this.countdown.setPosition(L.countdown.x, L.countdown.y).setOrigin(tox, 0);
    this.rerollBtn.setPosition(L.rerollBtn.x, L.rerollBtn.y);
    this.tipView.setPosition(L.tip.x, L.tip.y);
    this.tipView.relayout(dir);
    this.scrollList.resize(L.scroll.x, L.scroll.y, L.scroll.width, L.scroll.height);
    this.scrollList.setContentHeight(L.contentHeight);
    this.resultText.setPosition(L.result.x, L.result.y).setOrigin(tox, 0).setAlign(wrapAlign);
    const positions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[] = [];
    this.cells.forEach((cell, i) => {
      const c = L.cells[i]!;
      cell.name.setOrigin(L.cellTextOriginX, 0);
      cell.detail.setOrigin(L.cellTextOriginX, 0).setAlign(wrapAlign).setWordWrapWidth(c.detail.wrapWidth);
      cell.discountBadge
        .setOrigin(dir === "rtl" ? 0 : 1, 0);
      positions.push(
        { obj: cell.bg, lx: c.bg.x, ly: c.bg.y },
        { obj: cell.icon, lx: c.icon.x, ly: c.icon.y },
        { obj: cell.name, lx: c.name.x, ly: c.name.y },
        { obj: cell.detail, lx: c.detail.x, ly: c.detail.y },
        { obj: cell.buyBtn, lx: c.buyBtn.x, ly: c.buyBtn.y },
        { obj: cell.discountBadge, lx: c.discountBadge.x, ly: c.discountBadge.y },
      );
    });
    this.scrollList.relayoutContent(positions);
  }

  /** 셸 펼치기/접기 — 콘텐츠 rect(상단) 갱신 후 relayout으로 6슬롯 그리드 재배치(bottomY=서브탭 위, 불변). */
  resize(rect: TabContentRect): void {
    this.rect.x = rect.x;
    this.rect.y = rect.y;
    this.rect.width = rect.width;
    this.relayout(getTextDirection());
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    setVisibleActive(this.title, visible);
    setVisibleActive(this.countdown, visible);
    this.rerollBtn.setVisible(visible);
    this.tipView.setVisible(visible);
    this.scrollList.setVisible(visible);
    setVisibleActive(this.resultText, visible);
    for (const cell of this.cells) this.setCellVisible(cell, visible);
  }
}
